import { useEffect, useMemo, useState } from 'react';
import { useFrame } from 'react-frame-component';
import {
  Breakpoint,
  BreakpointKey,
  Breakpoints,
} from '@template/theme/types/breakpoints.type.ts';
import { useTheme } from 'styled-components';
import { throttle } from '@helpers/throttle.ts';

type TBreakpoint = {
  breakpoint: BreakpointKey;
  width: number;
  check: boolean;
  mounted: boolean;
};

const getCurrentBreakpoint = (
  breakpoints: Breakpoints,
  window?: Window
): TBreakpoint => {
  if (!window) {
    return {
      breakpoint: 'xs',
      width: 0,
      check: false,
      mounted: false,
    };
  }

  const width = window.innerWidth - 1;
  const breakpointKeys: BreakpointKey[] = Object.keys(
    breakpoints
  ).reverse() as BreakpointKey[];

  const currentBreakpoint =
    (breakpointKeys.find((key: BreakpointKey) => {
      const bp: Breakpoint = breakpoints[key] as Breakpoint;
      return width >= (bp.value || (bp as any));
    }) as BreakpointKey) || 'xs';

  return {
    breakpoint: currentBreakpoint,
    width,
    check: false,
    mounted: true,
  };
};

const checkBreakpoint = (
  targetBreakpoint: BreakpointKey,
  breakpoints: Breakpoints,
  width: number
): boolean => {
  if (!targetBreakpoint) return false;

  const targetBreakpointWidth =
    breakpoints[targetBreakpoint].value ||
    (breakpoints[targetBreakpoint] as any);

  return width >= targetBreakpointWidth;
};

const useBreakpoint = (targetBreakpoint: BreakpointKey) => {
  const { breakpoints } = useTheme();
  const frame = useFrame();

  const target = frame
    ? frame.window
    : typeof window !== 'undefined'
      ? window
      : undefined;

  const [currentState, setCurrentState] = useState<TBreakpoint>(() =>
    getCurrentBreakpoint(breakpoints, target)
  );

  const handleResize = useMemo(
    () =>
      throttle(() => {
        const newBreakpoint = getCurrentBreakpoint(breakpoints, target);
        setCurrentState({
          ...newBreakpoint,
          check: checkBreakpoint(
            targetBreakpoint,
            breakpoints,
            newBreakpoint.width
          ),
        });
      }, 200),
    [breakpoints, target, targetBreakpoint]
  );

  useEffect(() => {
    if (target) {
      target.addEventListener('resize', handleResize);
      handleResize(); // Update state on mount
    }
    return () => {
      target?.removeEventListener('resize', handleResize);
    };
  }, [target, handleResize]);

  return currentState;
};

export default useBreakpoint;
